import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, MoreHorizontal, Pencil, Trash2, HelpCircle, Plus, Clock, X, Sparkles } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Variation } from "@/hooks/useVariations";
import { usePlanLimits } from "@/hooks/usePlanLimits";
import { getTierConfig } from "@/lib/subscription";

interface VariationDropdownProps {
  variations: Variation[];
  selectedVariationId: string | null; // null = "Default"
  onSelect: (variationId: string | null) => void;
  onCreate?: () => void;
  onCancelPending?: (variation: Variation) => void;
  onRename?: (variation: Variation) => void;
  onDelete?: (variation: Variation) => void;
  isReadOnly?: boolean;
}

export function VariationDropdown({
  variations,
  selectedVariationId,
  onSelect,
  onCreate,
  onCancelPending,
  onRename,
  onDelete,
  isReadOnly = false,
}: VariationDropdownProps) {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);

  const { tier, variationLimit } = usePlanLimits();
  // Fall back to 10 while limits are loading to avoid flashing "at limit" UI.
  const effectiveLimit = variationLimit ?? 10;
  const tierName = getTierConfig(tier).name;
  const isUnlimitedTier = tier === "unlimited";

  const selectedVariation = selectedVariationId
    ? variations.find((v) => v.id === selectedVariationId)
    : null;
  const displayLabel = selectedVariation ? selectedVariation.name : "Default";

  const used = variations.length;
  const isAtLimit = used >= effectiveLimit;
  const remaining = Math.max(0, effectiveLimit - used);
  const hasVariations = used > 0;

  const handleUpgrade = () => {
    setIsOpen(false);
    navigate("/plans");
  };

  // Empty state — render a standalone "Create variation" button so the action is
  // visible without users having to discover the dropdown.
  if (!hasVariations) {
    if (isReadOnly || !onCreate) return null;
    return (
      <div className="flex flex-col items-start md:items-end gap-1">
        <div className="flex items-center gap-1">
          <span className="text-xs text-primary-foreground/60 uppercase tracking-wide">
            Variations
          </span>
          <TooltipProvider>
            <Tooltip open={isTooltipOpen} onOpenChange={setIsTooltipOpen}>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={() => setIsTooltipOpen(!isTooltipOpen)}
                  className="focus:outline-none"
                >
                  <HelpCircle className="w-3.5 h-3.5 text-primary-foreground/40 hover:text-primary-foreground/60 cursor-help" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="max-w-xs">
                <p>Create a variation to test alternate copy, audiences, or designs alongside your default email.</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={onCreate}
          className="h-8 px-3 text-primary-foreground bg-primary-foreground/10 hover:bg-primary-foreground/20 hover:text-primary-foreground"
        >
          <Plus className="mr-1.5 h-4 w-4" />
          Create variation
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-start md:items-end gap-1">
      <div className="flex items-center gap-1">
        <span className="text-xs text-primary-foreground/60 uppercase tracking-wide">
          Variations
        </span>
        <TooltipProvider>
          <Tooltip open={isTooltipOpen} onOpenChange={setIsTooltipOpen}>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={() => setIsTooltipOpen(!isTooltipOpen)}
                className="focus:outline-none"
              >
                <HelpCircle className="w-3.5 h-3.5 text-primary-foreground/40 hover:text-primary-foreground/60 cursor-help" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="max-w-xs">
              <p>Create variations from the menu, or send to prefix+tagname@... directly. Variations let you compare alternate copy, audiences, or designs.</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 px-3 text-primary-foreground bg-primary-foreground/10 hover:bg-primary-foreground/20 hover:text-primary-foreground"
          >
            <span className="max-w-[160px] truncate flex items-center gap-1.5">
              {selectedVariation?.pending && <Clock className="h-3.5 w-3.5 opacity-70" />}
              {displayLabel}
            </span>
            <ChevronDown className="ml-2 h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64 bg-card">
          {/* Default option */}
          <DropdownMenuItem
            className={cn(
              "cursor-pointer",
              selectedVariationId === null && "bg-muted"
            )}
            onClick={() => {
              onSelect(null);
              setIsOpen(false);
            }}
          >
            <span className="font-medium">Default</span>
          </DropdownMenuItem>

          <DropdownMenuSeparator />

          {variations.map((variation) => (
            <div
              key={variation.id}
              className={cn(
                "flex items-center justify-between px-2 py-1.5 text-sm cursor-pointer rounded-sm hover:bg-muted gap-2",
                selectedVariationId === variation.id && "bg-muted"
              )}
            >
              <button
                className="flex-1 text-left truncate flex flex-col min-w-0"
                onClick={() => {
                  onSelect(variation.id);
                  setIsOpen(false);
                }}
              >
                <span className="flex items-center gap-2 min-w-0">
                  <span
                    className={cn(
                      "truncate",
                      variation.pending && "text-muted-foreground italic"
                    )}
                  >
                    {variation.name}
                  </span>
                  {variation.pending && (
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide text-muted-foreground bg-muted-foreground/10 rounded px-1.5 py-0.5 flex-shrink-0">
                      <Clock className="h-3 w-3" />
                      Awaiting
                    </span>
                  )}
                </span>
              </button>

              {!isReadOnly && variation.pending && onCancelPending && (
                <button
                  className="p-1 rounded hover:bg-muted-foreground/20"
                  title="Cancel pending variation"
                  onClick={(e) => {
                    e.stopPropagation();
                    onCancelPending(variation);
                    setIsOpen(false);
                  }}
                >
                  <X className="h-4 w-4 text-muted-foreground" />
                </button>
              )}

              {!isReadOnly && !variation.pending && (onRename || onDelete) && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      className="p-1 rounded hover:bg-muted-foreground/20"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <MoreHorizontal className="h-4 w-4 text-muted-foreground" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-32 bg-card">
                    {onRename && (
                      <DropdownMenuItem
                        className="cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          onRename(variation);
                          setIsOpen(false);
                        }}
                      >
                        <Pencil className="mr-2 h-4 w-4" />
                        Rename
                      </DropdownMenuItem>
                    )}
                    {onDelete && (
                      <DropdownMenuItem
                        className="cursor-pointer text-destructive focus:text-destructive"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDelete(variation);
                          setIsOpen(false);
                        }}
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        Delete
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          ))}

          {!isReadOnly && onCreate && !isAtLimit && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="cursor-pointer"
                onClick={() => {
                  onCreate();
                  setIsOpen(false);
                }}
              >
                <Plus className="mr-2 h-4 w-4" />
                Create variation
              </DropdownMenuItem>
            </>
          )}

          {/* Footer: usage hint or upgrade banner */}
          {!isReadOnly && (
            <>
              {!isAtLimit && (
                <div className="px-2 py-1.5 text-[11px] text-muted-foreground">
                  {remaining} of {isUnlimitedTier ? "∞" : effectiveLimit} remaining on {tierName} plan
                </div>
              )}

              {isAtLimit && isUnlimitedTier && (
                <div className="px-2 py-1.5 text-[11px] text-muted-foreground">
                  Maximum {effectiveLimit} variations reached
                </div>
              )}

              {isAtLimit && !isUnlimitedTier && (
                <div className="mx-1 my-1 rounded-md bg-muted p-2.5 flex flex-col gap-2">
                  <div className="flex items-start gap-2">
                    <Sparkles className="h-3.5 w-3.5 mt-0.5 text-foreground flex-shrink-0" />
                    <p className="text-xs text-foreground leading-snug">
                      You've reached your <span className="font-medium">{tierName}</span> plan limit of {effectiveLimit} variation{effectiveLimit === 1 ? "" : "s"} per version.
                    </p>
                  </div>
                  <Button
                    size="sm"
                    onClick={handleUpgrade}
                    className="h-8 w-full bg-foreground text-background hover:bg-acid-green hover:text-foreground"
                  >
                    Upgrade for more variations
                  </Button>
                </div>
              )}
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
